/**
 * Permissions Module
 *
 * @module permissions
 * @category Permissions
 *
 * Handles permission and role checks:
 * - Role-based access control (RBAC)
 * - Permission checks (fast, local)
 * - Data scope queries (async, server-side)
 */

import type { HttpClient } from "@amaster.ai/http-client";
import type { User } from "../types";

export interface PermissionsModuleDeps {
  http: HttpClient;
  getCurrentUser: () => User | null;
  storage: { getItem: (key: string) => string | null };
}

export function createPermissionsModule(deps: PermissionsModuleDeps) {
  const { getCurrentUser } = deps;

  return {
    /**
     * Check if user has a specific role (fast, local check)
     *
     * @category Permissions
     * @performance O(1) - Fast
     * @example
     * ```typescript
     * if (permissions.hasRole("admin")) {
     *   showAdminPanel();
     * }
     * ```
     */
    hasRole(roleCode: string): boolean {
      const user = getCurrentUser();
      if (!user || !user.roles) return false;
      return user.roles.includes(roleCode);
    },

    /**
     * Check if user has a specific permission (fast, local check)
     *
     * @category Permissions
     * @performance O(1) - Fast
     * @example
     * ```typescript
     * if (permissions.hasPermission("user", "delete")) {
     *   showDeleteButton();
     * }
     * ```
     */
    hasPermission(resource: string, action: string): boolean {
      const user = getCurrentUser();
      if (!user || !user.permissions) return false;
      const permissionName = `${resource}.${action}`;
      return user.permissions.includes(permissionName);
    },

    /**
     * Check if user has ANY of the specified permissions
     *
     * @category Permissions
     * @performance O(n) - Fast
     * @example
     * ```typescript
     * if (permissions.hasAnyPermission([{ resource: "user", action: "read" }, { resource: "user", action: "write" }])) {
     *   showUserSection();
     * }
     * ```
     */
    hasAnyPermission(permissions: Array<{ resource: string; action: string }>): boolean {
      return permissions.some(({ resource, action }) => this.hasPermission(resource, action));
    },

    /**
     * Check if user has ALL of the specified permissions
     *
     * @category Permissions
     * @performance O(n) - Fast
     * @example
     * ```typescript
     * if (permissions.hasAllPermissions([{ resource: "user", action: "read" }, { resource: "user", action: "write" }])) {
     *   showFullEditor();
     * }
     * ```
     */
    hasAllPermissions(permissions: Array<{ resource: string; action: string }>): boolean {
      return permissions.every(({ resource, action }) => this.hasPermission(resource, action));
    },
  };
}

export type PermissionsModule = ReturnType<typeof createPermissionsModule>;
